import {
  Stack,
  Typography,
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  SxProps,
  TableSortLabel,
  TableBody,
  TablePagination,
  Pagination,
  Button,
} from "@mui/material";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import { useState, useMemo } from "react";
import SecondPositiveST from "./SecondPositiveST";
import SecondNegetiveST from "./SecondNegetiveST";
import SecondSaveChangesAi from "./SecondSaveChangesAi";
import { useSelector } from "react-redux";
import { Theme } from "@emotion/react";
import { formatDate } from "../../../../../../../../../utils/helpers";
import { useSTOAIRecommendations } from "../../../../../STOperationhooks/useSTOOperationAI";
import { setDisplayErrorSnackBar } from "../../../../../../../../../features/userSlice";
import { useDispatch } from "react-redux";
import { useSearchTermsTab } from "../../../../../STOperationhooks/useSearchTermsTab";
import {
  resetDefinitions,
  // setActionLoader,
} from "../../../../../../../../../features/aiRecomendationTabSlice";
import { useSearchParams } from "react-router-dom";

const SecondTableAi = () => {
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedRow, setSelectedRow] = useState<any>(null);
  const [openNegativeST, setOpenNegativeST] = useState(false);
  const [OpensaveTerm, setOpensaveTerm] = useState(false);
  const [page, setPage] = useState(0);
  const [sort, setSort] = useState<{
    keytoSort: string;
    direction: "asc" | "desc";
  }>({ keytoSort: "search_term", direction: "desc" });
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const dispatch = useDispatch();

  const {
    allSTLevels,
    selectedPositiveKeywordDefinitions,
    selectedNegativeKeywordDefinitions,
    selectedSearchTerm,
  } = useSelector((state: any) => state.aiRecomandationTab);

  const {
    getPositiveKeywordDetails,
    getNegetiveKeywordDetails,
    getAllSTLevel,
    removeNegativeSearchTerm,
  } = useSTOAIRecommendations();

  const [searchParams] = useSearchParams();
  const scopeSharedUid = searchParams.get("scopeUid") || "";
  // Helper function to transform data for API
  const transformDataForAPI = (dataList: any[]) => {
    return dataList.map((item: any) => ({
      search_term: item.searchTerm,
      search_term_match_type: item.matchType,
      keyword_id: item.keyword_id,
      scope_shared_uid: scopeSharedUid, // Get from URL params
      keyword_shared_uid: item.keyword_shared_uid,
      adgroup_shared_uid: item.adgroup_shared_uid,
    }));
  };

  const {
    createPositiveSearchTermLinks,
    createNegativeSearchTermLinks,
    activateOrdeactivateExistingLinks,
  } = useSearchTermsTab();

  const filteredAndSortedData = useMemo(() => {
    if (!Array.isArray(allSTLevels)) return [];

    const search =
      typeof selectedSearchTerm === "string"
        ? selectedSearchTerm.trim().toLowerCase()
        : "";

    if (!search) return allSTLevels;

    return allSTLevels.filter((row: any) =>
      row.search_term?.toLowerCase().includes(search)
    );
  }, [allSTLevels, selectedSearchTerm]);

  // Sort and paginate data
  const sortedData = useMemo(() => {
    if (!allSTLevels || !Array.isArray(allSTLevels) || allSTLevels.length === 0)
      return [];

    const sorted = [...filteredAndSortedData].sort((a: any, b: any) => {
      const aValue = a[sort.keytoSort];
      const bValue = b[sort.keytoSort];

      if (aValue === bValue) return 0;

      const comparison = aValue > bValue ? 1 : -1;
      return sort.direction === "asc" ? comparison : -comparison;
    });

    return sorted;
  }, [allSTLevels, sort, filteredAndSortedData]);

  const paginatedRows = useMemo(() => {
    const start = page * rowsPerPage;
    return sortedData.slice(start, start + rowsPerPage);
  }, [sortedData, page, rowsPerPage]);

  const handleHeaderClick = (header: any) => {
    if (!header.sortable) return;

    setSort((prev) => ({
      keytoSort: header.KEY,
      direction:
        prev.keytoSort === header.KEY && prev.direction === "asc"
          ? "desc"
          : "asc",
    }));
  };

  const handleChangePage = (_: any, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleOpenDialog = (row: any) => {
    getPositiveKeywordDetails(row.search_term, row.search_term_match_type);
    setSelectedRow(row);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setSelectedRow(null);
    setOpenDialog(false);
  };

  const handleOpenNegativeST = (row: any) => {
    getNegetiveKeywordDetails(row.search_term, row.search_term_match_type);
    setSelectedRow(row);
    setOpenNegativeST(true);
  };

  const handleCloseNegativeST = () => {
    setSelectedRow(null);
    setOpenNegativeST(false);
  };

  const handleOpenSaveTerm = () => {
    setOpensaveTerm(true);
  };

  const handleCloseSaveTerm = () => {
    setSelectedRow(null);
    setOpensaveTerm(false);
  };

  // Handle confirm from SecondSaveChangesAi
  const handleConfirmChanges = async (groupedData: any) => {
    let totalSuccess = 0;
    let totalFailed = 0;
    let failedSharedUids: string[] = [];

    try {
      // === Set as Exclusive ===
      if (groupedData.exclusive?.length > 0) {
        const transformedData = transformDataForAPI(groupedData.exclusive);

        try {
          const { successCount, failCount } =
            await createPositiveSearchTermLinks(
              transformedData,
              false,
              "Exclusive"
            );

          totalSuccess += successCount;
          totalFailed += failCount;
        } catch {
          totalFailed += groupedData.exclusive.length;
        }
      }

      // === Set as Allow ===
      if (groupedData.allow?.length > 0) {
        const transformedData = transformDataForAPI(groupedData.allow);

        try {
          const { successCount, failCount } =
            await createPositiveSearchTermLinks(
              transformedData,
              false,
              "Allowed"
            );

          totalSuccess += successCount;
          totalFailed += failCount;
        } catch {
          totalFailed += groupedData.allow.length;
        }
      }

      // === Deactivate Positive → Add to Negative List ===
      const negativeList = groupedData.negativeList ?? [];

      if (negativeList.length > 0) {
        try {
          const deactivateUids = negativeList
            .map((item: any) => item.search_term_shared_uid)
            .filter(Boolean);

          if (deactivateUids.length > 0) {
            const {
              successCount: deactivateSuccess,
              failCount: deactivateFail,
              successfulUids: deactivatedUids,
            } = await activateOrdeactivateExistingLinks(deactivateUids, false);

            totalFailed += deactivateFail;

            // Only create negatives for successfully deactivated terms
            if (deactivateSuccess > 0 && deactivatedUids.length > 0) {
              const deactivatedUidsSet = new Set(deactivatedUids);
              // Filter negativeData to only include successfully deactivated items
              const successfullyDeactivatedItems = negativeList.filter(
                (item: any) =>
                  deactivatedUidsSet.has(item.search_term_shared_uid)
              );
              const reqBody = {
                terms: successfullyDeactivatedItems.map((item: any) => ({
                  term: item.searchTerm,
                  match_type: item.matchType,
                  level: "ad_group",
                  level_shared_uid: item.adgroup_shared_uid,
                  additional_info: {
                    search_term_shared_uid: item.search_term_shared_uid,
                  },
                })),
              };

              const { successCount, failCount, failedSearchTermUids } =
                await createNegativeSearchTermLinks(reqBody);

              failedSharedUids = failedSearchTermUids;
              totalSuccess += successCount;
              totalFailed += failCount;
            }
          }
        } catch {
          totalFailed += negativeList.length;
        } finally {
          // Re-activate failed items
          if (failedSharedUids && failedSharedUids.length > 0) {
            activateOrdeactivateExistingLinks(failedSharedUids, true);
          }
        }
      }

      // === Remove Negative ===
      if (groupedData.removeNegative?.length > 0) {
        const reqBody = {
          terms: groupedData.removeNegative.map((item: any) => ({
            shared_uid: item.search_term_shared_uid,
          })),
        };

        try {
          const { successCount, failCount } = await removeNegativeKeywords(
            reqBody
          );

          totalSuccess += successCount;
          totalFailed += failCount;
        } catch {
          totalFailed += groupedData.removeNegative.length;
        }
      }

      // === Final UX Decision ===
      if (totalSuccess > 0) {
        await getAllSTLevel();
        dispatch(resetDefinitions());
        handleCloseSaveTerm();

        dispatch(
          setDisplayErrorSnackBar({
            message: `Processed ${totalSuccess} item(s).${
              totalFailed > 0 ? ` ${totalFailed} failed.` : ""
            }`,
            type: "success",
          })
        );
        return;
      }

      if (totalFailed > 0) {
        dispatch(
          setDisplayErrorSnackBar({
            message: `All ${totalFailed} item(s) failed to process.`,
            type: "error",
          })
        );
      }
    } catch (error) {
      dispatch(
        setDisplayErrorSnackBar({
          message:
            error instanceof Error
              ? error.message
              : "Critical failure while processing AI recommendations.",
          type: "error",
        })
      );
    }
  };

  // Helper function for removing negative keywords (add this as a new function)
  const removeNegativeKeywords = async (reqBody: any) => {
    let successCount = 0;
    let failCount = 0;

    try {
      const response = await removeNegativeSearchTerm(reqBody);

      successCount = response?.successCount ?? 0;
      failCount = response?.failCount ?? 0;
    } catch (error) {
      // If API fails entirely, all terms failed
      failCount = reqBody?.terms?.length ?? 0;
      successCount = 0;
    }

    return { successCount, failCount };
  };

  // Check if there are any changes to save
  const hasChanges =
    Object.keys(selectedPositiveKeywordDefinitions || {}).length > 0 ||
    Object.keys(selectedNegativeKeywordDefinitions || {}).length > 0;

  // Define table headers
  const tableHeaders = [
    { id: 1, KEY: "search_term", label: "Search Term", sortable: true },
    {
      id: 2,
      KEY: "search_term_match_type",
      label: "Match Type",
      sortable: true,
    },
    { id: 3, KEY: "last_seen", label: "Last Seen", sortable: true },
    {
      id: 4,
      KEY: "impression_last_30_days",
      label: "IMP",
      sortable: true,
    },
    { id: 5, KEY: "clicks_last_30_days", label: "Clicks", sortable: true },
    { id: 6, KEY: "cost_last_30_days", label: "Cost", sortable: true },
    {
      id: 7,
      KEY: "positive_keyword_count",
      label: "Positive ST",
      sortable: true,
    },
    {
      id: 8,
      KEY: "negative_keyword_count",
      label: "Negative ST",
      sortable: true,
    },
  ];

  return (
    <>
      <Stack flexDirection={"column"} alignItems={"flex-end"} gap={2}>
        <TableContainer
          component={Paper}
          sx={{
            boxShadow: "none",
            mt: 2,
          }}
        >
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                {tableHeaders.map((header: any) => {
                  const isLeftSticky = header.id === 1 || header.id === 2;

                  const leftOffsets: Record<number, number> = {
                    1: 0,
                    2: 150,
                  };

                  const cellSx = isLeftSticky
                    ? ({
                        position: "sticky",
                        left: leftOffsets[header.id] ?? 0,
                        zIndex: 10,
                        backgroundColor: "background.paper",
                      } as SxProps<Theme>)
                    : ({} as SxProps<Theme>);

                  return (
                    <TableCell key={header.id} align="left" sx={cellSx}>
                      <TableSortLabel
                        active={sort.keytoSort === header.KEY}
                        direction={
                          sort.keytoSort === header.KEY ? sort.direction : "asc"
                        }
                        onClick={() => handleHeaderClick(header)}
                        disabled={!header.sortable}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        {header.label}
                      </TableSortLabel>
                    </TableCell>
                  );
                })}
              </TableRow>
            </TableHead>

            <TableBody>
              {paginatedRows.length > 0 ? (
                paginatedRows.map((row: any, index: number) => {
                  return (
                    <TableRow key={index}>
                      <TableCell
                        sx={{
                          position: "sticky",
                          left: 0,
                          zIndex: 1,
                          whiteSpace: "normal",
                          overflowWrap: "break-word",
                          wordBreak: "break-word",
                          backgroundColor: "background.paper",
                        }}
                      >
                        {row.search_term ?? " "}
                      </TableCell>
                      <TableCell
                        sx={{
                          position: "sticky",
                          left: 150,
                          zIndex: 1,
                          whiteSpace: "normal",
                          overflowWrap: "break-word",
                          wordBreak: "break-word",
                          backgroundColor: "background.paper",
                        }}
                      >
                        {row.search_term_match_type ?? " "}
                      </TableCell>
                      <TableCell>{formatDate(row.last_seen)}</TableCell>
                      <TableCell>
                        {row.impression_last_30_days?.toLocaleString() ?? " "}
                      </TableCell>
                      <TableCell>
                        {row.clicks_last_30_days?.toLocaleString() ?? " "}
                      </TableCell>
                      <TableCell>
                        {row.cost_last_30_days
                          ? `${row.cost_last_30_days.toFixed(2)}$`
                          : ""}
                      </TableCell>
                      <TableCell>
                        <Typography
                          data-tracker="614"
                          variant="body2"
                          sx={{
                            fontSize: "0.99rem",
                            fontWeight: 500,
                            color:
                              row.positive_keyword_count > 0
                                ? "green"
                                : "default",
                            cursor:
                              row.positive_keyword_count > 0
                                ? "pointer"
                                : "default",
                            textDecoration:
                              row.positive_keyword_count > 0
                                ? "underline"
                                : "none",
                            "&:hover": {
                              textDecoration:
                                row.positive_keyword_count > 0
                                  ? "underline"
                                  : "none",
                            },
                          }}
                          onClick={() => {
                            if (row.positive_keyword_count > 0) {
                              handleOpenDialog(row);
                            }
                          }}
                        >
                          {row.positive_keyword_count ?? 0}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          data-tracker="616"
                          variant="body2"
                          sx={{
                            fontSize: "0.99rem",
                            fontWeight: 500,
                            color:
                              row.negative_keyword_count > 0
                                ? "red"
                                : "default",
                            cursor:
                              row.negative_keyword_count > 0
                                ? "pointer"
                                : "default",
                            textDecoration:
                              row.negative_keyword_count > 0
                                ? "underline"
                                : "none",
                            "&:hover": {
                              textDecoration:
                                row.negative_keyword_count > 0
                                  ? "underline"
                                  : "none",
                            },
                          }}
                          onClick={() => {
                            if (row.negative_keyword_count > 0) {
                              handleOpenNegativeST(row);
                            }
                          }}
                        >
                          {row.negative_keyword_count ?? 0}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={tableHeaders.length} align="center">
                    No data available
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Stack>

      <Stack
        direction="row"
        justifyContent="flex-end"
        alignItems="center"
        mt={2}
        mb={2}
      >
        <Button
          variant="contained"
          color="secondary"
          endIcon={<SaveOutlinedIcon />}
          onClick={handleOpenSaveTerm}
          disabled={!hasChanges}
          sx={{ ml: 2 }}
          data-tracker="618"
        >
          Save changes
        </Button>
      </Stack>
      {paginatedRows.length > 0 && (
        <Stack
          spacing={1}
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ p: 1 }}
        >
          <TablePagination
            rowsPerPageOptions={[]}
            component="div"
            count={sortedData.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              ".MuiInputBase-root-MuiTablePagination-select,.MuiTablePagination-selectLabel":
                {
                  display: "none",
                },
              ".MuiTablePagination-actions": { display: "none" },
              ".MuiTablePagination-toolbar": { paddingLeft: "16px" },
            }}
          />

          <Pagination
            count={Math.ceil(sortedData.length / rowsPerPage)}
            onChange={(_, value) => handleChangePage(null, value - 1)}
            page={page + 1}
            showFirstButton
            showLastButton
            color="secondary"
            size="small"
          />

          <TablePagination
            rowsPerPageOptions={[10, 25, 100]}
            component="div"
            count={sortedData.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            classes={{
              displayedRows: "hidden-displayed-rows",
            }}
            sx={{
              ".MuiTablePagination-actions": { display: "none" },
              ".MuiTablePagination-input": { marginRight: "15px" },
            }}
          />
        </Stack>
      )}

      <SecondPositiveST
        open={openDialog}
        rowData={selectedRow}
        onClose={handleCloseDialog}
      />
      <SecondNegetiveST
        open={openNegativeST}
        rowData={selectedRow}
        onClose={handleCloseNegativeST}
      />
      <SecondSaveChangesAi
        open={OpensaveTerm}
        rowData={selectedRow}
        onClose={handleCloseSaveTerm}
        onConfirm={handleConfirmChanges}
      />
    </>
  );
};

export default SecondTableAi;
